import React, { useState } from "react";
import { useStoreState } from "ezStore";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { gql } from "__generated__/gql";
import { useApolloClient } from "@apollo/client";

import { Table } from "@cofano/cck";


import { TableId, useBackendTable } from "components/table";

import { LocationLink, ProductLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import StatusChanger from "view/statuses/components/changer/StatusChanger";
import BulkStatusChanger from "view/statuses/components/changer/BulkStatusChanger";
import { DataFormat, hasPluginPermission, usePermission } from "utils";
import UpdateContainerLoadedDialog from "view/containers/components/dialogs/UpdateContainerLoadedDialog";
import UpdateContainerDischargedDialog from "view/containers/components/dialogs/UpdateContainerDischargedDialog";
import { CallContainersBlockProps } from "view/voyages/components/details/VoyageDetailsCallContainersBlock";

import { Call_ContainersQuery } from "__generated__/graphql";

import { ChildPropsFromPagination } from "types/commons";
import { SortDirection, CallType, StatusType } from "__generated__/enums";

type CallContainersProps = {
	type: CallType;
} & Pick<CallContainersBlockProps, "call">

type Container = ChildPropsFromPagination<Call_ContainersQuery, "cargos">

const VoyageDetailsCallContainers = ({ call, type }: CallContainersProps) => {

	const navigate = useNavigate();
	const client = useApolloClient();
	const { isSkipperOrShipManager, isReadOnly } = usePermission();

	const session = useStoreState(state => state.session);

	const [ dialogOpen, openDialog ] = useState<boolean>(false);
	const [ selection, setSelection ] = useState<Array<Container>>([]);

	const { tableProps, refetch } = useBackendTable({
		id: TableId.CallContainers,
		selectSettings: {
			isFullSelectEnabled: false,
		},
		onSelect: (newSelection) => setSelection(newSelection.map(it => ({ ...it, id: it.id ?? 0 }))),
		onChange: async (pagination, sort, _, callback) => {
			try {
				const result = await client.query({
					query: CONTAINERS_QUERY,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "created",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							loadingCalls: type === CallType.Loading ? [ call.id ] : undefined,
							dischargingCalls: type === CallType.Discharging ? [ call.id ] : undefined,
							excludeExpired: false,
						},
					},
				});

				callback({
					content: result.data.cargos.content,
					totalResults: result.data.cargos.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	return (
		<React.Fragment>
			<Table
				{...tableProps}
				onRowDoubleClick={row => navigate(`/view/cargos/${row.id}/details`)}
				columns={[ {
					id: "containerNumber",
					title: intl.formatMessage({ id: "CONTAINER_NUMBER" }),
				}, {
					id: "containerType",
					title: intl.formatMessage({ id: "CARGO_CONTAINER_TYPE" }),
					render: (row) => DataFormat.containerType(row.containerType),
				}, {
					id: "currentStatuses",
					title: intl.formatMessage({ id: "STATUS" }),
					render: (row) => {
						return (
							<StatusChanger
								session={session}
								entity={row}
								statusType={StatusType.Cargo}
								refetch={refetch} />
						);
					},
					sortable: true,
					sortKey: "lastStatusChange.status.description",
					disabled: !hasPluginPermission(session, "cargoStatuses"),
					width: 175,
				}, {
					id: "loadingLocation",
					title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
					render: row => <LocationLink location={row.firstLoadingAction?.location} />,
					disabled: type === CallType.Discharging,
				}, {
					id: "dischargingLocation",
					title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
					render: row => <LocationLink location={row.lastDischargingAction?.location} />,
					disabled: type === CallType.Loading,
				}, {
					id: "loadingReference",
					title: intl.formatMessage({ id: "LOADING_REFERENCE" }),
					disabled: type === CallType.Discharging,
				}, {
					id: "dischargingReference",
					title: intl.formatMessage({ id: "DISCHARGING_REFERENCE" }),
					disabled: type === CallType.Loading,
				}, {
					id: "isEmpty",
					title: intl.formatMessage({ id: "CARGO_FULL_OR_EMPTY" }),
					render: (row) => row.empty ? intl.formatMessage({ id: "CARGO_EMPTY" }) : intl.formatMessage({ id: "CARGO_FULL" }),
				}, {
					id: "weight",
					title: intl.formatMessage({ id: "CARGO_WEIGHT" }),
					render: (row) => DataFormat.tonnage(row.totalLoadingWeight),
				}, {
					id: "product",
					title: intl.formatMessage({ id: "PRODUCT" }),
					render: (row) => <ProductLink product={row.product} />,
				}, {
					id: "loaded",
					title: intl.formatMessage({ id: "CARGO_LOADED" }),
					render: (row) => <FormattedDate date={row.loaded} />,
					disabled: type === CallType.Discharging,
					width: 140,
				}, {
					id: "discharged",
					title: intl.formatMessage({ id: "CARGO_DISCHARGED" }),
					render: (row) => <FormattedDate date={row.discharged} />,
					disabled: type === CallType.Loading,
					width: 140,
				} ]}
				actions={ !isReadOnly ? [ {
					render: (selection: Container[] | null, isFullSelect) => {
						return (
							!isFullSelect && (
								<BulkStatusChanger
									statusType={StatusType.Cargo}
									//@ts-ignore inconsistency between generated types and the handwritten ones (StatusChange's)
									entities={selection ?? []}
									refetch={refetch} />
							)
						);
					},
				}, {
					title: intl.formatMessage({ id: type === CallType.Loading ? "UPDATE_LOADED" : "UPDATE_DISCHARGED" }),
					onClick: () => openDialog(true),
					disabled: (selection) => {
						return isSkipperOrShipManager && selection.find(it => type === CallType.Loading ? it.loaded : it.discharged);
					},
				} ] : undefined} />

			{type === CallType.Loading && (
				<UpdateContainerLoadedDialog
					open={dialogOpen}
					onClose={() => openDialog(false)}
					refetch={() => refetch()}
					containers={selection} />
			)}

			{type === CallType.Discharging && (
				<UpdateContainerDischargedDialog
					open={dialogOpen}
					onClose={() => openDialog(false)}
					refetch={() => refetch()}
					containers={selection} />
			)}
		</React.Fragment>
	);
};

const CONTAINERS_QUERY = gql(`#graphql
	query call_containers($search: CargoSearch!, $pagination: Pagination) {
		cargos(search: $search, pagination: $pagination) {
			totalElements
			content {
				id
				lastStatusChange {
					...statusChangeFragment
				}
				firstLoadingAction {
					...CargoActionWithVoyage
				}
				lastDischargingAction {
					...CargoActionWithVoyage
				}
				containerNumber
				loadingReference
				dischargingReference
				containerType {
					isoTypeCode
					displayCode
				}
				empty
				loaded
				discharged
				canSkipperEditLoadedTimestamp
				canSkipperEditDischargedTimestamp
				product {
					id
					name
				}
				totalLoadingWeight
			}
		}
	}
`);


export default VoyageDetailsCallContainers;
